import { Button, Card, Chip, Link } from "@heroui/react";
import { Trans } from "@lingui/react/macro";
import { useNavigate, useNavigation, useRouteError } from "@remix-run/react";
import { ArrowLeft, CircleAlert, CircleHelp, Home, MessageCircle, RefreshCcw } from "lucide-react";
import { nanoid } from "nanoid";
import { useEffect } from "react";
import { ClientOnly } from "remix-utils/client-only";
import { useIntercom } from "~/integration/intercom";

function NotFoundCard() {
  const navigate = useNavigate();

  return (
    <div className="flex flex-col items-center gap-6 p-8 md:p-12">
      <div className="rounded-full bg-blue-50-50 p-4">
        <CircleHelp
          className="w-12 h-12 text-blue"
          aria-label="Page not found"
        />
      </div>

      <div className="space-y-4 text-center">
        <h1 className="text-3xl font-semibold text-foreground">
          <Trans>We Couldn't Find This Page</Trans>
        </h1>

        <p className="text-large text-foreground-500">
          <Trans>The page you're looking for doesn't exist or has been moved.</Trans>
        </p>
      </div>

      <div className="flex w-full gap-2">
        <Button
          startContent={<ArrowLeft />}
          color="default"
          variant="flat"
          size="lg"
          onPress={() => navigate(-1)}
          className="flex-1"
        >
          <Trans>Go Back</Trans>
        </Button>
        <Button
          as={Link}
          href="/"
          startContent={<Home />}
          color="primary"
          variant="solid"
          size="lg"
          className="flex-1"
        >
          <Trans>Go Home</Trans>
        </Button>
      </div>
    </div>
  );
}

function ErrorCard() {
  const navigate = useNavigate();
  const errorCode = nanoid(6).toUpperCase();
  const timestamp = new Date().toLocaleString();

  return (
    <div className="flex flex-col items-center gap-6 p-8 md:p-12">
      <div className="rounded-full bg-warning-50 p-4">
        <CircleAlert
          className="w-12 h-12 text-warning"
          aria-label="Warning indicator"
        />
      </div>

      <div className="space-y-4 text-center">
        <h1 className="text-3xl font-semibold text-foreground">
          <Trans>Oops! We Hit a Small Bump</Trans>
        </h1>

        <p className="text-large text-foreground-500">
          <Trans>Our team has been automatically notified and is investigating the issue.</Trans>
        </p>

        <div className="flex gap-2 justify-center">
          <Chip
            variant="flat"
            size="sm"
            color="default"
          >
            <Trans>Error Code: {errorCode}</Trans>
          </Chip>
          <Chip
            variant="flat"
            size="sm"
            color="default"
          >
            <Trans>Time: {timestamp}</Trans>
          </Chip>
        </div>
      </div>

      <div className="w-full space-y-4">
        <h2 className="text-lg font-medium text-foreground">
          <Trans>What you can try:</Trans>
        </h2>
        <ul className="list-disc pl-6 space-y-2 text-foreground-600">
          <li>
            <Trans>Wait a few minutes and try again</Trans>
          </li>
          <li>
            <Trans>Check your internet connection</Trans>
          </li>
          <li>
            <Trans>Clear your browser cache and refresh the page</Trans>
          </li>
          <li>
            <Trans>Try using a different browser, or a different device</Trans>
          </li>
        </ul>
      </div>

      <div className="flex w-full gap-2">
        <Button
          startContent={<ArrowLeft />}
          color="default"
          variant="flat"
          size="lg"
          onPress={() => navigate(-1)}
          className="flex-1"
        >
          <Trans>Go Back</Trans>
        </Button>
        <Button
          startContent={<RefreshCcw />}
          color="primary"
          variant="solid"
          size="lg"
          onPress={() => window.location.reload()}
          className="flex-1"
        >
          <Trans>Try Again</Trans>
        </Button>
      </div>

      <div className="w-full border-t border-divider pt-6 space-y-4">
        <h2 className="text-lg font-medium text-foreground">
          <Trans>Technical Support:</Trans>
        </h2>

        <p className="text-sm text-foreground-500">
          <Trans>
            For technical issues, contact Eversa Support at{" "}
            <Link
              href="mailto:support@eversa.app"
              size="sm"
              className="text-primary"
              underline="hover"
            >
              support@eversa.app
            </Link>{" "}
            or start a live chat below.
          </Trans>
        </p>
        <Button
          startContent={<MessageCircle />}
          color="primary"
          variant="ghost"
          size="lg"
          fullWidth
          onPress={() => {
            if (window.Intercom) {
              window.Intercom(
                "showNewMessage",
                `I'm facing an issue with the application. I received an error message and would like some help resolving it.\n\nError Code: ${errorCode}\nTimestamp: ${timestamp}\nURL: ${window.location.href}`,
              );
            }
          }}
        >
          <Trans>Start Live Chat</Trans>
        </Button>
      </div>
    </div>
  );
}

export default function RootErrorBoundary() {
  const navigation = useNavigation();
  const error = useRouteError() as { status?: number };
  const is404 = error?.status === 404;

  useEffect(() => {
    // Don't log 404 errors
    if (is404) return;

    const errorDetails = {
      message: "An error occurred",
      error: typeof error === "string" ? error : JSON.stringify(error),
      namespace: navigation.location?.pathname,
      errorCode: nanoid(6).toUpperCase(),
      errorUrl: window.location.href,
    };

    if (window.Intercom) {
      window.Intercom("update", {
        last_error: errorDetails.error,
        last_error_path: errorDetails.namespace,
        last_error_url: window.location.href,
      });
    }

    console.error(errorDetails);
  }, [error, navigation.location, is404]);

  return (
    <>
      <ClientOnly>
        {() => {
          useIntercom();

          return (
            <>
              <div
                className="flex flex-1 min-h-[48rem] max-h-screen w-full items-center justify-start overflow-hidden p-2 sm:p-4 lg:p-8"
                style={{
                  backgroundImage: "url(/wall-with-black-squares.avif)",
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              >
                <div className="w-full max-w-2xl mx-auto">
                  <Card className="bg-white backdrop-blur-md shadow-large">
                    {is404 ? <NotFoundCard /> : <ErrorCard />}
                  </Card>
                </div>

                {/* Testimonial */}
                <div className="absolute bottom-10 right-10 hidden md:block">
                  <p className="max-w-xl text-right text-white/60">
                    <span className="font-medium">"</span>
                    <Trans>
                      We strive to provide the best experience possible. Our team is here to help
                      you every step of the way.
                    </Trans>
                    <span className="font-medium">"</span>
                  </p>
                </div>
              </div>
            </>
          );
        }}
      </ClientOnly>
    </>
  );
}
